"use client";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "next/navigation";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

interface props {
  count: number;
  rowsPerPage: number;
}

export default function Pagination({ count, rowsPerPage }: props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { replace } = useRouter();

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    replace(`${pathname}?${params.toString()}`);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");
  const totalPages = Math.ceil(count / rowsPerPage);


  // Function to generate pagination links with truncation
  const renderPageLinks = () => {
    const links = [];
    const visiblePageNumbers = [];
    const maxVisiblePages = 5; // Maximum number of visible pages excluding ellipses
    let startPage = Math.max(currentPage - Math.floor(maxVisiblePages / 2), 1);
    let endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(endPage - maxVisiblePages + 1, 1);
    }

    if (startPage > 1) {
      links.push(
        <a
          key={1}
          href="#"
          onClick={() => handlePageChange(1)}
          className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          1
        </a>
      );
      if (startPage > 2) {
        links.push(
          <span
            key="startEllipsis"
            className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500"
          >
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      visiblePageNumbers.push(i);
    }

    visiblePageNumbers.forEach((pageNumber) => {
      links.push(
        <a
          key={pageNumber}
          href="#"
          onClick={() => handlePageChange(pageNumber)}
          className={`inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm ${
            currentPage === pageNumber
              ? "text-gray-700 font-semibold"
              : "text-gray-500 hover:text-gray-700"
          }`}
          aria-current={currentPage === pageNumber ? "page" : undefined}
        >
          {pageNumber}
        </a>
      );
    });

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        links.push(
          <span
            key="endEllipsis"
            className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500"
          >
            ...
          </span>
        );
      }
      links.push(
        <a
          key={totalPages}
          href="#"
          onClick={() => handlePageChange(totalPages)}
          className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          {totalPages}
        </a>
      );
    }

    return links;
  };

  const start = count === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, count);


  return (
    <div>
      <p className="text-xs text-gray-500 my-4">
        Mostrando {start} - {end} de {count} resultados
      </p>
      { totalPages > 1 && (
      <nav className="flex items-center justify-between  px-4 sm:px-0">
        <div className="-mt-px flex w-0 flex-1">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            className={`inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
            disabled={currentPage === 1}
            aria-disabled={currentPage === 1}
          >
            <MdKeyboardArrowLeft className="mr-3 h-5 w-5" aria-hidden="true" />
            Anterior
          </button>
        </div>
        <div className="hidden md:-mt-px md:flex">{renderPageLinks()}</div>
        <div className="-mt-px flex w-0 flex-1 justify-end">
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            className={`inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
            disabled={currentPage === totalPages}
            aria-disabled={currentPage === totalPages}
          >
            Siguiente
            <MdKeyboardArrowRight className="ml-3 h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </nav>
      )}
    </div>
  );
}
